/**
 * The Desk's right rail: the money due in the next seven days, and the
 * reminder that the list beside it can be worked from Claude.
 *
 * The money comes from the same calendar the Money page and the athlete's
 * portal read (shared/moneyCalendar through api/moneyCalendar): paychecks
 * from each contract's roster week, deal payments from each deal's schedule.
 * Every row carries the calendar's own sentence about where its figure came
 * from; nothing is derived again here. Amounts the record does not hold show
 * as an em dash and are counted, not guessed.
 *
 * Assistants are kept out of money by the collection rules, so for them the
 * rail says so instead of showing an empty panel.
 */

import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { getAgencyMoneyCalendar } from '../../api/moneyCalendar'
import type { AgencyMoneyItem } from '../../shared/agencyMoney'
import { money } from '../../utils/format'
import styles from '../../pages/Dashboard.module.css'

const KIND: Record<AgencyMoneyItem['kind'], string> = {
  paycheck: 'Paycheck',
  deal_payment: 'Deal payment',
  received: 'Received',
}

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

/** Today through six days on, as YYYY-MM-DD, in the browser's own day. */
export function nextSevenDays(now = new Date()): { from: string; to: string } {
  const end = new Date(now)
  end.setDate(end.getDate() + 6)
  return { from: iso(now), to: iso(end) }
}

export function dueInWindow(items: AgencyMoneyItem[], window: { from: string; to: string }): AgencyMoneyItem[] {
  return items
    .filter(i => i.date >= window.from && i.date <= window.to)
    .sort((a, b) => a.date.localeCompare(b.date) || a.client_name.localeCompare(b.client_name))
}

function shortDay(date: string): string {
  const [y, m, d] = date.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' })
}

export function MoneyThisWeek({ enabled, isAssistant }: { enabled: boolean; isAssistant: boolean }) {
  const q = useQuery({
    queryKey: ['money-calendar', 'next-7-days'],
    queryFn: () => getAgencyMoneyCalendar(null, 14),
    enabled: enabled && !isAssistant,
  })
  const window = nextSevenDays()
  const due = dueInWindow(q.data?.items ?? [], window)
  const known = due.filter(i => i.amount != null)
  const total = known.reduce((s, i) => s + (i.amount as number), 0)

  return (
    <section className={styles.railPanel} aria-label="Money in the next seven days">
      <h2 className={styles.railTitle}>Money, next 7 days</h2>
      <p className={styles.railSub}>The whole book · from each contract’s roster week and each deal’s schedule</p>
      {isAssistant ? <p className={styles.railEmpty}>Money is not shown to assistants.</p>
        : q.isLoading ? <p className={styles.railEmpty}>Reading the calendar…</p>
          : q.error ? <p className={styles.railEmpty} role="alert">The calendar could not be read: {(q.error as Error).message}</p>
            : due.length === 0 ? <p className={styles.railEmpty}>Nothing is due in the next seven days.</p>
              : (
                <>
                  <ul className={styles.railList}>
                    {due.map(i => (
                      <li key={`${i.kind}:${i.source_id}:${i.date}`} className={styles.railRow}>
                        <div className={styles.railWhen}>{shortDay(i.date)}</div>
                        <div className={styles.railWhat}>
                          <Link to={`/clients/${i.client_id}`} className={styles.railWho}>{i.client_name}</Link>
                          <div className={styles.railNote}>{KIND[i.kind]}{i.detail ? ` · ${i.detail}` : ''}</div>
                        </div>
                        <div className={styles.railAmt}>{money(i.amount)}</div>
                      </li>
                    ))}
                  </ul>
                  <div className={styles.railTotal}>
                    <span>{known.length === due.length ? 'Total' : `Total of ${known.length} with a known amount`}</span>
                    <strong>{money(total)}</strong>
                  </div>
                </>
              )}
      <Link to="/revenue" className={styles.railLink}>Open Money</Link>
    </section>
  )
}

export function SayItToClaude() {
  return (
    <section className={styles.railPanel} aria-label="Work this list from Claude">
      <h2 className={styles.railTitle}>Say it to Claude instead</h2>
      <p className={styles.railSub}>
        Everything on this list can be settled from Claude through the Reppr connector — “What needs me today?”,
        “Tucker’s check arrived” — and the answer comes from the same record, so the two never disagree.
      </p>
      <Link to="/settings?tab=integrations" className={styles.railLink}>Connect Claude</Link>
    </section>
  )
}
